import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Plus } from 'lucide-react';
import { AnimatedIcon, IconMotion } from '../components/AnimatedIcon';
import { useTranslation } from 'react-i18next';
import { tourService } from '../services/tourService';
import { motion, AnimatePresence } from 'framer-motion';

export interface HubOption {
  label: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  onClick?: () => void;
  tourId?: string;
}

interface HubPageProps {
  title: string;
  subtitle: string;
  options: HubOption[];
  onMount?: () => void;
  tourPhaseName?: string;
}

// Design minimal v1: cada módulo ganha um tom pastel (escurecido no modo escuro) e uma micro-animação própria
const cardThemes: { tint: string; motion: IconMotion }[] = [
  { tint: 'bg-tint-sage', motion: 'draw' },
  { tint: 'bg-tint-lavender', motion: 'pop' },
  { tint: 'bg-tint-peach', motion: 'wobble' },
  { tint: 'bg-tint-sky', motion: 'bounce' },
  { tint: 'bg-tint-butter', motion: 'spin' },
  { tint: 'bg-tint-rose', motion: 'pulse' },
  { tint: 'bg-tint-stone', motion: 'swing' },
];

const ease = [0.22, 1, 0.36, 1] as const;

export const HubPage: React.FC<HubPageProps> = ({ title, subtitle, options, onMount, tourPhaseName }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0);

  const onMountRef = useRef(onMount);
  useEffect(() => {
    onMountRef.current = onMount;
  }, [onMount]);

  useEffect(() => {
    if (onMountRef.current) {
      onMountRef.current();
    }
  }, []);

  const handleAccess = async (opt: HubOption) => {
    if (opt.onClick) opt.onClick();
    
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter(name => name !== 'whisper-stt-cache' && name !== 'nllb-onnx-models-v1' && name !== 'free-dict-cache' && name !== 'rich-dict-cache-v2' && name !== 'bergamot-models-v1' && name !== 'marian-onnx-models-v1' && name !== 'transformers-cache' && name !== 'piper-voices-cache')
            .map(name => caches.delete(name))
        );
      } catch (e) {
        console.warn('Cache clear failed:', e);
      }
    }
    
    navigate(opt.path);
  };

  useEffect(() => {
    if (!tourPhaseName) return;
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === tourPhaseName) {
        const steps = options.filter(o => o.tourId).map(o => ({
          element: `#${o.tourId}`,
          popover: { title: o.label, description: o.description, showButtons: ['next'] }
        }));
        if (steps.length > 0) tourService.createDriver(steps);
      }
    };
    const timeoutId = setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => { clearTimeout(timeoutId); window.removeEventListener('libritui-tour-started', handleTour); };
  }, [tourPhaseName, options]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-ink px-5 md:px-8 pt-10 pb-36 flex flex-col items-center select-none font-sans">
      {/* Cabeçalho: rótulo mono + título grande e leve com ponto de destaque */}
      <motion.header
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="w-full max-w-xl mb-8"
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
            {String(options.length).padStart(2, '0')} {t('hubs.modules', 'módulos')}
          </span>
          <span className="h-px flex-1 bg-ink/10" />
        </div>
        <h1 className="font-display text-[44px] md:text-6xl font-light tracking-[-0.045em] leading-[0.95] text-ink">
          {title}<span className="text-primary">.</span>
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted leading-relaxed max-w-md">
          {subtitle}
        </p>
      </motion.header>

      {/* Lista de módulos: cartões separados; o selecionado ganha o tom pastel da seção */}
      <div className="w-full max-w-xl flex flex-col gap-2.5">
        {options.map((opt, idx) => {
          const isSelected = selectedIndex === idx;
          const theme = cardThemes[idx % cardThemes.length];

          return (
            <motion.div
              key={idx}
              id={opt.tourId}
              layout
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ layout: { type: 'spring', stiffness: 380, damping: 34 }, opacity: { duration: 0.5, delay: 0.08 + idx * 0.05 }, y: { duration: 0.6, ease, delay: 0.08 + idx * 0.05 } }}
              whileHover={isSelected ? undefined : { y: -2 }}
              onClick={() => setSelectedIndex(isSelected ? null : idx)}
              className={`relative cursor-pointer select-none rounded-[28px] transition-colors duration-500 ${
                isSelected
                  ? `${theme.tint} p-6 md:p-7`
                  : 'bg-surface hover:bg-surface/70 p-4 md:p-5 border border-black/[0.04] dark:border-white/[0.04]'
              }`}
            >
              {/* Linha do módulo */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center transition-colors duration-500 ${
                    isSelected ? 'bg-ink text-surface' : `${theme.tint} text-ink`
                  }`}>
                    <AnimatedIcon motion={theme.motion} active={isSelected}>
                      {React.isValidElement(opt.icon)
                        ? React.cloneElement(opt.icon as React.ReactElement<any>, { size: 19, strokeWidth: 1.75 })
                        : opt.icon}
                    </AnimatedIcon>
                  </div>
                  <span className={`truncate text-[15px] md:text-base tracking-[-0.01em] ${isSelected ? 'font-semibold text-ink' : 'font-medium text-ink/85'}`}>
                    {opt.label}
                  </span>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[11px] text-muted tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
                  <motion.span
                    animate={{ rotate: isSelected ? 45 : 0 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 26 }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-ink/10 text-ink' : 'text-muted'}`}
                  >
                    <Plus size={16} strokeWidth={1.75} />
                  </motion.span>
                </div>
              </div>

              {/* Conteúdo expandido */}
              <AnimatePresence initial={false}>
                {isSelected && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35, ease }}
                    className="overflow-hidden"
                  >
                    <p className="pt-4 pb-5 text-sm leading-relaxed text-ink/70 max-w-md">
                      {opt.description}
                    </p>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAccess(opt);
                      }}
                      className="group w-full h-12 px-5 rounded-full bg-primary text-slate-900 text-sm font-medium flex items-center justify-between cursor-pointer hover:opacity-90"
                    >
                      <span>{t('hubs.access', 'Acessar Agora')}</span>
                      <span className="w-8 h-8 rounded-full bg-surface/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                        <ArrowUpRight size={16} strokeWidth={1.75} />
                      </span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HubPage;
